// ============================================================================
// Authenticated equipment fueling identity bridge
// ============================================================================
// The equipment fueling surfaces are login-required, but their presenters
// intentionally remain in src/webforms without AuthContext coupling. This is
// the narrow app-side seam that owns the authenticated identity and injects
// only the plain submitter value the presenters actually consume.
//
// Do not move this into src/webforms. FuelingHub owns route-local equipment
// data, and the forms own their commands, but neither may choose or inspect an
// authenticated session. Keeping the identity here prevents App's global
// submitter object from becoming an input to the equipment surface tree.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';
import OwnerEquipmentReports from '../../equipment/OwnerEquipmentReports.js';

type FuelingPresenterProps = {
  sessionSubmitter?: SessionSubmitter;
  ownerReports?: React.ReactNode;
};

type FuelingAuthenticatedBridgeProps = {
  children: React.ReactElement<FuelingPresenterProps>;
};

export default function FuelingAuthenticatedBridge({children}: FuelingAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  // App's LoginScreen gate normally makes this branch unreachable. Keep the
  // local fail-closed guard so a future pre-gate route mount cannot inject a
  // public or stale identity into an authenticated write surface.
  if (authState === null || authState === false) return null;

  // This is intentionally the same display-name fallback the form controls
  // already receive. The bridge gives FuelingHub/FuelSupplyWebform a plain
  // value; it never passes the AuthContext identity itself into src/webforms.
  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return React.cloneElement(children, {sessionSubmitter, ownerReports: <OwnerEquipmentReports />});
}
